import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Cookie, Settings, Check, X, Shield, Info } from 'lucide-react';
import { handleMetaPixelConsentChange } from '../lib/metaPixel';

interface CookiePreferences {
  analytics: boolean;
  marketing: boolean;
}

export default function CookieConsent() {
  const [hasPreference, setHasPreference] = useState<boolean | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    analytics: false,
    marketing: false,
  });

  // Load initial state
  useEffect(() => {
    try {
      const savedPrefs = localStorage.getItem('cookie-consent-preferences');
      if (savedPrefs) {
        const parsed = JSON.parse(savedPrefs) as CookiePreferences;
        setPreferences(parsed);
        setHasPreference(true);
        // Do not auto-display banner if preference is already saved
        setIsOpen(false);
        // Initialize Meta Pixel tracking state if user previously consented to marketing
        handleMetaPixelConsentChange(parsed.marketing);
      } else {
        // No saved preference -> show the cookie popup
        setHasPreference(false);
        setIsOpen(true);
      }
    } catch (e) {
      console.error('Failed to parse cookie preferences from localStorage:', e);
      setHasPreference(false);
      setIsOpen(true);
    }
  }, []);

  const saveConsent = (updatedPrefs: CookiePreferences) => {
    try {
      localStorage.setItem('cookie-consent-preferences', JSON.stringify(updatedPrefs));
    } catch (e) {
      console.error('Failed to write to localStorage:', e);
    }

    setPreferences(updatedPrefs);
    setHasPreference(true);
    setIsOpen(false);
    setShowPreferences(false);

    // Update Meta Pixel Consent dynamically
    handleMetaPixelConsentChange(updatedPrefs.marketing);

    // Update GA4 Consent Mode
    if (typeof (window as any).gtag === 'function') {
      const analyticsStatus = updatedPrefs.analytics ? 'granted' : 'denied';
      const marketingStatus = updatedPrefs.marketing ? 'granted' : 'denied';

      (window as any).gtag('consent', 'update', {
        analytics_storage: analyticsStatus,
        ad_storage: marketingStatus,
        ad_user_data: marketingStatus,
        ad_personalization: marketingStatus,
      });

      // Optionally dispatch an event in dataLayer to signal consent update to custom GTM tags
      if (typeof (window as any).dataLayer !== 'undefined') {
        (window as any).dataLayer.push({
          event: 'consent_update',
          consent_analytics: updatedPrefs.analytics,
          consent_marketing: updatedPrefs.marketing,
        });
      }
    }
  };

  const handleAcceptAll = () => {
    const allEnabled = { analytics: true, marketing: true };
    saveConsent(allEnabled);
  };

  const handleRejectAll = () => {
    const allDisabled = { analytics: false, marketing: false };
    saveConsent(allDisabled);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const toggleToggle = (key: keyof CookiePreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // If initial load hasn't processed, avoid flash
  if (hasPreference === null) return null;

  return (
    <>
      {/* Floating Toggle Button to re-open settings at any time */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            id="cookie-consent-trigger"
            initial={{ scale: 0, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: 20 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-40 bg-agora-rust text-[#FDFCFB] p-3.5 rounded-full shadow-lg hover:bg-[#8D3C20] cursor-pointer transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-agora-rust group"
            title="Privacy & Cookie Preferences"
            aria-label="Cookies Preferences"
          >
            <Cookie className="h-5 w-5 group-hover:rotate-12 transition-transform duration-300" />
            <span className="sr-only">Cookies Preferences</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Main Cookie Banner Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="cookie-consent-card"
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ type: 'spring', damping: 20, stiffness: 150 }}
            className="fixed bottom-6 right-6 z-50 bg-[#FDFCFB] border border-agora-tan/60 shadow-2xl rounded-2xl p-5 w-[calc(100%-2rem)] sm:w-[400px] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-agora-rust/10 rounded-lg text-agora-rust">
                  <Cookie className="h-5 w-5 animate-pulse" />
                </div>
                <h3 className="font-serif text-lg font-bold text-agora-ink">Cookie Preferences</h3>
              </div>
              <button
                id="cookie-consent-close"
                onClick={() => {
                  // If they close without choosing, we don't save. But let's allow them to close
                  setIsOpen(false);
                }}
                className="text-agora-ink/40 hover:text-agora-ink transition-colors p-1 rounded-md"
                aria-label="Close preferences window"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Cookie Policy Description or Manage Preferences Tabs */}
            {!showPreferences ? (
              <div className="space-y-4">
                <p className="text-xs leading-relaxed text-agora-ink/75">
                  We use cookies to improve your browsing experience, understand how visitors use The Local Agora, and support optional marketing features. You can accept all cookies, reject non-essential cookies, or manage your preferences at any time.
                </p>

                {/* Main Action Buttons */}
                <div className="flex flex-col gap-2 pt-2">
                  <button
                    id="cookie-consent-accept-all"
                    onClick={handleAcceptAll}
                    className="w-full bg-agora-rust hover:bg-[#8D3C20] text-white py-2 px-4 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 cursor-pointer flex items-center justify-center gap-1.5 shadow-sm"
                  >
                    <Check className="h-3.5 w-3.5" /> Accept All
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      id="cookie-consent-reject-all"
                      onClick={handleRejectAll}
                      className="w-full bg-[#1A1A1A] hover:bg-black text-[#FDFCFB] py-2 px-4 rounded-xl text-xs font-medium tracking-wide transition-all duration-200 cursor-pointer"
                    >
                      Reject Non-Essential
                    </button>
                    <button
                      id="cookie-consent-manage-prefs"
                      onClick={() => setShowPreferences(true)}
                      className="w-full border border-agora-tan hover:bg-agora-tan/10 text-agora-ink/80 hover:text-agora-ink py-2 px-4 rounded-xl text-xs font-medium tracking-wide transition-all duration-200 cursor-pointer flex items-center justify-center gap-1"
                    >
                      <Settings className="h-3 w-3" /> Manage Preferences
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-xs text-agora-ink/60 flex items-center gap-1 bg-agora-tan/10 p-2 rounded-lg border border-agora-tan/20">
                  <Shield className="h-3.5 w-3.5 flex-shrink-0 text-agora-rust" /> Choose which optional cookies The Local Agora may use.
                </div>

                {/* Preference Toggle Items */}
                <div className="space-y-3 py-1">
                  
                  {/* Essential (Required) Toggle */}
                  <div className="flex items-center justify-between border-b border-agora-tan/15 pb-2.5">
                    <div className="flex-1 pr-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-agora-ink">Essential Cookies</span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-agora-ink/5 text-agora-ink/60 rounded font-mono font-bold uppercase tracking-wider">Required</span>
                      </div>
                      <p className="text-[10px] text-agora-ink/60 mt-0.5 leading-tight">Required for core website functions such as navigation, security, and preference storage.</p>
                    </div>
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-5 bg-agora-rust rounded-full opacity-60 flex items-center p-0.5 cursor-not-allowed">
                        <div className="w-4 h-4 bg-white rounded-full translate-x-4 shadow" />
                      </div>
                    </div>
                  </div>

                  {/* Analytics Toggle */}
                  <div className="flex items-center justify-between border-b border-agora-tan/15 pb-2.5">
                    <div className="flex-1 pr-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-agora-ink">Analytics Tracking</span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-agora-rust/5 text-agora-rust/80 rounded font-mono font-bold uppercase tracking-wider">Optional</span>
                      </div>
                      <p className="text-[10px] text-[#222222]/80 mt-0.5 leading-tight">Helps us understand website traffic, page views, and general usage so we can improve The Local Agora.</p>
                    </div>
                    <button
                      id="cookie-toggle-analytics"
                      onClick={() => toggleToggle('analytics')}
                      className={`w-9 h-5 rounded-full flex items-center p-0.5 cursor-pointer transition-colors duration-200 outline-none ${
                        preferences.analytics ? 'bg-agora-rust' : 'bg-agora-ink/25'
                      }`}
                      aria-label="Toggle analytic tracking cookies"
                    >
                      <motion.div
                        layout
                        className="w-4 h-4 bg-white rounded-full shadow"
                        animate={{ x: preferences.analytics ? 16 : 0 }}
                      />
                    </button>
                  </div>

                  {/* Marketing Toggle */}
                  <div className="flex items-center justify-between pb-1">
                    <div className="flex-1 pr-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-agora-ink">Marketing & Personalization</span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-agora-rust/5 text-agora-rust/80 rounded font-mono font-bold uppercase tracking-wider">Optional</span>
                      </div>
                      <p className="text-[10px] text-[#222222]/80 mt-0.5 leading-tight font-sans">May be used to measure advertising performance and support relevant promotions.</p>
                    </div>
                    <button
                      id="cookie-toggle-marketing"
                      onClick={() => toggleToggle('marketing')}
                      className={`w-9 h-5 rounded-full flex items-center p-0.5 cursor-pointer transition-colors duration-200 outline-none ${
                        preferences.marketing ? 'bg-agora-rust' : 'bg-agora-ink/25'
                      }`}
                      aria-label="Toggle marketing and personalization cookies"
                    >
                      <motion.div
                        layout
                        className="w-4 h-4 bg-white rounded-full shadow"
                        animate={{ x: preferences.marketing ? 16 : 0 }}
                      />
                    </button>
                  </div>

                </div>

                {/* Actions inside Preferences */}
                <div className="flex gap-2 pt-1 border-t border-agora-tan/20">
                  <button
                    id="cookie-prefs-back"
                    onClick={() => setShowPreferences(false)}
                    className="flex-1 py-2 px-3 border border-agora-tan hover:bg-agora-tan/10 text-agora-ink/80 hover:text-agora-ink rounded-xl text-xs font-medium transition-all duration-200 cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    id="cookie-prefs-save"
                    onClick={handleSavePreferences}
                    className="flex-1 py-2 px-3 bg-agora-rust hover:bg-[#8D3C20] text-white rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 cursor-pointer flex items-center justify-center gap-1"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
